import React, { useEffect, useMemo } from 'react';
import { titleEditorStoreSelector, useTitleEditorStore } from '@warehouse/title/domain';
import {
	createRecordPathCodec,
	IframeEmbedParams,
	QuickbaseIFrameContainer,
	quickbaseTabStoreSelector,
	recordPathSegmentFor,
	recordPresentationFor,
	TabType,
	useNxTensionStore,
	useQuickbaseHostBridgeSync,
} from '@warehouse/quickbase';

export function NxTension() {
	const title = useTitleEditorStore(titleEditorStoreSelector.title);

	// Store selectors
	const isLoading = useNxTensionStore(quickbaseTabStoreSelector.isLoading);
	const isAuthenticated = useNxTensionStore(quickbaseTabStoreSelector.isAuthenticated);
	const error = useNxTensionStore(quickbaseTabStoreSelector.error);
	const embedUrl = useNxTensionStore(quickbaseTabStoreSelector.embedUrl);
	const frameUrl = useNxTensionStore(quickbaseTabStoreSelector.frameUrl);
	const showAuthOverlay = useNxTensionStore(quickbaseTabStoreSelector.showAuthOverlay);
	const authFlow = useNxTensionStore(quickbaseTabStoreSelector.authFlow);
	const actions = useNxTensionStore(quickbaseTabStoreSelector.actions);

	const { authenticate, loadEmbedUrl, setShowAuthOverlay, dismissAuthOverlay, recoverSession } = actions;

	const codec = useMemo(
		() =>
			createRecordPathCodec({
				resolveBasePath: (pathname) => pathname.match(/^\/title\/[^/]+/)?.[0] ?? null,
				recordSegment: recordPathSegmentFor(TabType.NxTension),
			}),
		[],
	);

	const { bridge, initialEmbedParams, reloadToken } = useQuickbaseHostBridgeSync({
		embedUrl,
		codec,
		reloadEmbedUrl: (embedParams: IframeEmbedParams) => {
			if (title?.uuid) loadEmbedUrl(title.uuid, embedParams);
		},
		presentation: recordPresentationFor(TabType.NxTension),
	});

	// Load embed URL when title changes
	useEffect(() => {
		if (title?.uuid) {
			loadEmbedUrl(title.uuid, initialEmbedParams);
		}
	}, [title?.uuid, loadEmbedUrl, initialEmbedParams]);

	return (
		<QuickbaseIFrameContainer
			isLoading={isLoading}
			isAuthenticated={isAuthenticated}
			error={error}
			embedUrl={embedUrl}
			frameUrl={frameUrl}
			showAuthOverlay={showAuthOverlay}
			authenticate={authenticate}
			setShowAuthOverlay={setShowAuthOverlay}
			dismissAuthOverlay={dismissAuthOverlay}
			recoverSession={recoverSession}
			authFlow={authFlow}
			iFrameType="NxTension"
			bridge={bridge}
			reloadToken={reloadToken}
		/>
	);
}
